import * as THREE from 'three/webgpu';

import {
    resolveGlbDatumSlotFrame,
} from '../../imported-model/datum-slot';
import { isDatumAxis } from '../../reference/DatumAxis';
import { isDatumPlaneObject } from '../../reference/DatumPlane';
import { isDatumPoint } from '../../reference/DatumPoint';
import type { Viewer } from '../../viewer/Viewer';
import {
    MeasureDistancePrimitiveKind,
    type MeasureDistanceGeometry,
} from '../types/measure-distance-types';
import {
    MeasureUnboundedGeometryKind,
    type MeasureUnboundedGeometryBinding,
} from '../types/measure-unbounded-types';

/**
 * 无边界测量几何适配器。
 *
 * 职责：将 {@link MeasureUnboundedGeometryBinding} 转换为求距基元。
 */
export class MeasureUnboundedGeometryAdapter {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;

    /**
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
    }

    /**
     * 将无边界绑定适配为求距几何体。
     *
     * @param binding 无边界绑定。
     * @returns 求距几何体；无法适配时返回 null。
     */
    public adapt(binding: MeasureUnboundedGeometryBinding): MeasureDistanceGeometry | null {
        switch (binding.kind) {
            case MeasureUnboundedGeometryKind.GlbDatumSlotPlane:
                return this.adaptGlbDatumSlotPlane(binding);
            case MeasureUnboundedGeometryKind.GlbDatumSlotOrigin:
                return this.adaptGlbDatumSlotOrigin(binding);
            case MeasureUnboundedGeometryKind.FormalDatumPlane:
                return this.adaptFormalDatumPlane(binding);
            case MeasureUnboundedGeometryKind.FormalDatumAxis:
                return this.adaptFormalDatumAxis(binding);
            case MeasureUnboundedGeometryKind.FormalDatumPoint:
                return this.adaptFormalDatumPoint(binding);
            default:
                return null;
        }
    }

    /**
     * 适配 GLB datum-slot 平面。
     *
     * @param binding 无边界绑定。
     * @returns 无限平面基元。
     */
    private adaptGlbDatumSlotPlane(
        binding: MeasureUnboundedGeometryBinding,
    ): MeasureDistanceGeometry | null {
        const descriptor = binding.datumSlot;
        if (!descriptor) {
            return null;
        }

        const frame = resolveGlbDatumSlotFrame(this.viewer.scene, descriptor);
        if (!frame || frame.kind !== 'plane') {
            return null;
        }

        return {
            kind: MeasureDistancePrimitiveKind.Plane,
            point: frame.origin.clone(),
            normal: frame.normal.clone(),
        };
    }

    /**
     * 适配 GLB datum-slot 原点。
     *
     * @param binding 无边界绑定。
     * @returns 点基元。
     */
    private adaptGlbDatumSlotOrigin(
        binding: MeasureUnboundedGeometryBinding,
    ): MeasureDistanceGeometry | null {
        const descriptor = binding.datumSlot;
        if (!descriptor) {
            return null;
        }

        const frame = resolveGlbDatumSlotFrame(this.viewer.scene, descriptor);
        if (!frame || frame.kind !== 'origin') {
            return null;
        }

        return {
            kind: MeasureDistancePrimitiveKind.Point,
            position: frame.origin.clone(),
        };
    }

    /**
     * 适配正式基准面。
     *
     * @param binding 无边界绑定。
     * @returns 无限平面基元。
     */
    private adaptFormalDatumPlane(
        binding: MeasureUnboundedGeometryBinding,
    ): MeasureDistanceGeometry | null {
        const plane = this.viewer.getAllDatumPlanes()
            .find((candidate) => candidate.id === binding.datumPlaneId);
        const planeObject = plane?.getObject3D() ?? null;
        if (!planeObject || !isDatumPlaneObject(planeObject)) {
            return null;
        }

        let referencePlane: THREE.Plane | null = null;
        planeObject.traverse((child) => {
            if (referencePlane) {
                return;
            }
            const candidate = Reflect.get(child, 'referencePlane');
            if (candidate instanceof THREE.Plane) {
                referencePlane = candidate;
            }
        });
        if (!referencePlane) {
            return null;
        }

        const normal = referencePlane.normal.clone().normalize();
        const point = referencePlane.coplanarPoint(new THREE.Vector3());
        planeObject.localToWorld(point);
        normal.transformDirection(planeObject.matrixWorld).normalize();

        return {
            kind: MeasureDistancePrimitiveKind.Plane,
            point,
            normal,
        };
    }

    /**
     * 适配正式基准轴。
     *
     * @param binding 无边界绑定。
     * @returns 无限直线基元。
     */
    private adaptFormalDatumAxis(
        binding: MeasureUnboundedGeometryBinding,
    ): MeasureDistanceGeometry | null {
        const axis = (this.viewer.datumAxisManager?.getAllAxes() ?? [])
            .find((candidate) => candidate.id === binding.datumAxisId);
        const axisObject = axis?.getObject3D() ?? null;
        if (!axisObject || !isDatumAxis(axisObject)) {
            return null;
        }

        return {
            kind: MeasureDistancePrimitiveKind.InfiniteLine,
            origin: axisObject.originWorld.clone(),
            direction: axisObject.getWorldDirection(),
        };
    }

    /**
     * 适配正式基准点。
     *
     * @param binding 无边界绑定。
     * @returns 点基元。
     */
    private adaptFormalDatumPoint(
        binding: MeasureUnboundedGeometryBinding,
    ): MeasureDistanceGeometry | null {
        const point = (this.viewer.datumPointManager?.getAllPoints() ?? [])
            .find((candidate) => candidate.id === binding.datumPointId);
        const pointObject = point?.getObject3D() ?? null;
        if (!pointObject || !isDatumPoint(pointObject)) {
            return null;
        }

        return {
            kind: MeasureDistancePrimitiveKind.Point,
            position: pointObject.getWorldPoint().clone(),
        };
    }
}
